import React, { ReactNode, useContext } from 'react';
import { ListItem, Text, OrderedList, UnorderedList } from '@chakra-ui/react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { Options } from '@contentful/rich-text-react-renderer';
import { ILink } from '@packages/core-contentful-rest';

import {
  getGqlEntryHyperlink,
  getOrderedListStyleTypeByLevel,
  getUnorderedListStyleTypeByLevel,
  ListContext,
} from './defaultStyleHelpers';
import { CustomizeDefaultOptions, LinkEntry } from './types';
import { DefaultRichTextLink, ScrollLink } from './links';

const defaultRichTextRenderOptions = ({
  customRenderNodeOptions,
  customRenderTextOption,
  nodeProps,
  gqlLinks,
  onEntryHyperlinkClick,
}: CustomizeDefaultOptions): Options => {
  return {
    renderText: customRenderTextOption,
    renderMark: {
      [MARKS.SUPERSCRIPT]: (text: ReactNode) => <sup>{text}</sup>,
      [MARKS.BOLD]: (text: ReactNode) => (
        // for defaults, want bold style to apply font weight of 500
        <Text fontWeight={500} as="b">
          {text}
        </Text>
      ),
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (_, children: ReactNode) => (
        <Text
          as="h2" // for default we don't want to have h1 because it could negatively impact SEO
          textStyle={{
            base: 'Headline/Large/Primary regular',
            md: 'Display/Small/Primary',
          }}
          // Display/Small/Primary has font wieght of 600, overriding to reduce to 400
          sx={{
            fontWeight: { base: 400 },
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h1}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_2]: (_, children: ReactNode) => (
        <Text
          as="h2"
          textStyle={{
            base: 'Headline/Medium/Primary regular',
            md: 'Headline/Extra large/Primary regular',
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h2}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_3]: (_, children: ReactNode) => (
        <Text
          as="h3"
          textStyle={{
            base: 'Headline/Small/Primary regular',
            md: 'Headline/Large/Primary regular',
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h3}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_4]: (_, children: ReactNode) => (
        <Text
          as="h4"
          textStyle={{
            base: 'Headline/Extra small/Primary regular',
            md: 'Headline/Medium/Primary regular',
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h4}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_5]: (_, children: ReactNode) => (
        <Text
          as="h5"
          textStyle={{
            base: 'Headline/Extra small/Primary regular',
            md: 'Headline/Small/Primary regular',
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h5}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_6]: (_, children: ReactNode) => (
        <Text
          as="h6"
          textStyle={{
            base: 'Body/Large/Regular',
            md: 'Headline/Extra small/Primary regular',
          }}
          {...nodeProps?.allText}
          {...nodeProps?.allHeaders}
          {...nodeProps?.h6}
        >
          {children}
        </Text>
      ),
      [BLOCKS.PARAGRAPH]: (_, children: ReactNode) => (
        <Text
          textStyle="Body/Medium/Regular"
          {...nodeProps?.allText}
          {...nodeProps?.p}
        >
          {children}
        </Text>
      ),
      [INLINES.HYPERLINK]: (node, children: ReactNode) => {
        if (!node?.data?.uri) return;
        if (node?.data?.uri.includes('scrollAnchor=')) {
          // This link text indicates link should scroll to some element on current page
          return <ScrollLink url={node.data.uri}>{children}</ScrollLink>;
        }

        return (
          <DefaultRichTextLink href={node.data.uri}>
            {children}
          </DefaultRichTextLink>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children: ReactNode) => {
        const entryLink: LinkEntry | undefined =
          (node?.data?.target as ILink)?.fields ||
          getGqlEntryHyperlink(node.data.target, gqlLinks);
        if (!entryLink?.url) return;
        if (entryLink?.url?.includes('scrollAnchor=')) {
          // This link text indicates link should scroll to some element on current page
          return <ScrollLink url={entryLink.url}>{children}</ScrollLink>;
        }
        return (
          <DefaultRichTextLink
            href={entryLink.url}
            onClick={
              onEntryHyperlinkClick
                ? (event) =>
                    onEntryHyperlinkClick(event, {
                      forceOpenSameTab: entryLink.forceOpenSameTab,
                    })
                : undefined
            }
          >
            {children}
          </DefaultRichTextLink>
        );
      },
      [BLOCKS.UL_LIST]: (_, children: ReactNode) => {
        const List: React.FunctionComponent = () => {
          const { listLevel } = useContext(ListContext);
          const newListLevel = listLevel + 1;
          return (
            <ListContext.Provider value={{ listLevel: newListLevel }}>
              <UnorderedList
                styleType={getUnorderedListStyleTypeByLevel(newListLevel)}
                marginBlockStart={0}
                marginBlockEnd={0}
                textAlign={'left'}
                paddingInlineStart={0}
                sx={{
                  columnCount: nodeProps?.ul?.columnCount,
                  'li > *:not(ol):not(ul)': {
                    display: 'list-item',
                    listStylePosition: 'inside',
                  },
                }}
                {...nodeProps?.ul?.listProps}
              >
                {children}
              </UnorderedList>
            </ListContext.Provider>
          );
        };

        return <List />;
      },
      [BLOCKS.OL_LIST]: (_, children: ReactNode) => {
        const List: React.FunctionComponent = () => {
          const { listLevel } = useContext(ListContext);
          const newListLevel = listLevel + 1;
          return (
            <ListContext.Provider value={{ listLevel: newListLevel }}>
              <OrderedList
                styleType={getOrderedListStyleTypeByLevel(newListLevel)}
                marginBlockStart={0}
                marginBlockEnd={0}
                textAlign={'left'}
                paddingInlineStart={0}
                sx={{
                  'li > *:not(ol):not(ul)': {
                    display: 'list-item',
                    listStylePosition: 'inside',
                  },
                }}
              >
                {children}
              </OrderedList>
            </ListContext.Provider>
          );
        };

        return <List />;
      },
      [BLOCKS.LIST_ITEM]: (_, children: ReactNode) => {
        return (
          <ListItem display={'block'} {...nodeProps?.li}>
            {children}
          </ListItem>
        );
      },
      ...customRenderNodeOptions,
    },
  };
};

export { defaultRichTextRenderOptions };
